/*!
 * @license
 * SPDX-License-Identifier: LicenseRef-cloudEQ-Proprietary
 * Copyright (c) 2026-present cloudEQ Inc. All Rights Reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL.
 *
 * This source code, including all associated files, documentation, and
 * compiled forms, is the exclusive property of cloudEQ Inc. and is
 * protected by copyright, trade secret, and other intellectual property laws.
 *
 * This software is licensed, not sold, solely for use by authorized
 * licensees under the terms of a written license agreement with
 * cloudEQ Inc. Unless expressly permitted by that agreement, you may not
 * copy, modify, merge, distribute, sublicense, sell, publish, reverse
 * engineer, decompile, disassemble, or create derivative works of this
 * software, in whole or in part, by any means.
 *
 * Access to this file does not grant any license or right to use it.
 * Unauthorized use, reproduction, or disclosure is strictly prohibited and
 * may result in civil and criminal penalties.
 *
 * Contact: legal@cloudeq.com
 */

import { useEffect, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { INTRO_KEY, INTRO_VERSION, shouldShowIntro } from './push';
import type { Push } from './usePush';

/**
 * The one-time introduction.
 *
 * **Nobody opens a menu item they do not know appeared.** Push does not switch
 * itself on, so without this the feature ships and almost nobody has it. The
 * alternative was a message somebody sends and everybody has to keep; this is
 * the app introducing itself instead — once, and then never again.
 *
 * Four rules, each of which is a way this could have gone wrong:
 *
 *  - **Once per device, not once per person.** The subscription is per device,
 *    so somebody who turned it on for their phone still needs to know it exists
 *    on their laptop
 *  - **Never shown to somebody who already has it on.** Nothing reads as more
 *    automated than being told about a thing you are already using
 *  - **"Not now" means never.** It does not come back next week. The account
 *    menu row is where they turn it on later, which is why that row shows state
 *  - **It waits for the board.** Never over the sign-in screen and never while
 *    the page is still empty — the first thing somebody sees after signing in
 *    should be their work, not a request
 */
export function PushIntro({ push, onExplain }: { push: Push; onExplain: () => void }) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (push.loading) return;

    let seen: string | null = null;
    // A locked-down profile can make localStorage throw on read. Treating that
    // as "never seen" would show the card on every single load, which is far
    // worse than never showing it.
    try {
      seen = localStorage.getItem(INTRO_KEY);
    } catch {
      return;
    }
    if (!shouldShowIntro(push.state, seen)) return;

    // **A beat after the board has painted.** Mounting this synchronously puts
    // a dialog over a half-drawn page, and on a slow connection it lands before
    // the first card does — so the first thing somebody sees after signing in
    // is a request rather than their work.
    const t = setTimeout(() => setOpen(true), 1200);
    return () => clearTimeout(t);
  }, [push.loading, push.state]);

  // Written the moment it is shown, not when it is dismissed. Somebody who
  // closes the tab on it has still seen it, and showing it again tomorrow is
  // exactly the behaviour "once" rules out.
  const remember = () => {
    try {
      localStorage.setItem(INTRO_KEY, String(INTRO_VERSION));
    } catch {
      /* the card simply reappears next time; not worth failing the click */
    }
  };

  const close = () => {
    remember();
    setOpen(false);
  };

  /**
   * **One tap, and it does the right thing from wherever they are.**
   *
   * Where a permission prompt can work, this goes straight to it. Where it
   * cannot — an iPhone in a Safari tab, a browser that was blocked once, an
   * unsupported one — it opens the explanation instead, by exactly the same rule
   * as the switch in the menu: never offer a control that silently fails.
   */
  const turnOn = () => {
    remember();
    setOpen(false);
    if (push.state === 'off') void push.enable();
    else onExplain();
  };

  return (
    <Dialog.Root open={open} onOpenChange={(o) => { if (!o) close(); }}>
      <Dialog.Portal>
        <Dialog.Overlay id="push-intro-overlay" className="motion-backdrop open fixed inset-0 z-[80] bg-black/40" />
        <Dialog.Content
          id="push-intro"
          className="motion-sheet fixed left-1/2 top-1/2 z-[81] w-[min(400px,92vw)] -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-2xl bg-surface shadow-overlay"
        >
          <BellArt />

          <div className="px-6 pb-5 pt-4 text-center">
            <Dialog.Title asChild>
              <h2 id="push-intro-title" className="m-0 text-lg font-semibold tracking-[-0.01em] text-ink">
                Qollab can notify you now
              </h2>
            </Dialog.Title>
            <Dialog.Description className="m-0 mt-2 text-sm leading-relaxed text-ink-muted">
              Get a notification when you are assigned something, when somebody
              comments, or when work you are waiting on is finished — even with the
              tab closed.
            </Dialog.Description>

            <div className="mt-5 flex flex-col gap-1.5">
              <button
                id="push-intro-enable"
                type="button"
                className="btn btn-primary w-full"
                onClick={turnOn}
              >
                Turn on notifications
              </button>
              {/* Quiet, and not a second primary. "Not now" is a perfectly good
                  answer and the card must not argue with it. */}
              <button
                id="push-intro-dismiss"
                type="button"
                className="tap rounded-lg px-3 py-2 text-sm font-semibold text-ink-faint transition-colors duration-fast ease-out-soft hover:text-ink-muted"
                onClick={close}
              >
                Not now
              </button>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/**
 * The illustration.
 *
 * **Inline SVG built from the palette, not a new binary.** It has to work in
 * both themes, and `index.css` already defines every colour it needs — a PNG
 * would be a second thing to keep in step with the palette, and it would be
 * wrong in one theme the day either changes.
 *
 * **It carries no text of its own.** Text inside an illustration cannot be
 * translated, cannot be read by a screen reader, and goes stale the day the
 * wording above it changes. The two grey bars are a card, not lettering.
 *
 * `aria-hidden`, because everything it says is said in words underneath it.
 */
function BellArt() {
  return (
    <div className="relative grid h-36 place-items-center overflow-hidden bg-action-bg">
      {/* Two soft discs behind the mark, drawn from the accent at low opacity —
          the same gradient language as the board's own surfaces, so this reads
          as part of the product rather than as clip art. */}
      <div className="pointer-events-none absolute -left-8 -top-10 size-32 rounded-full bg-action/10" aria-hidden />
      <div className="pointer-events-none absolute -bottom-12 -right-6 size-36 rounded-full bg-teal/10" aria-hidden />

      <svg
        width="96"
        height="96"
        viewBox="0 0 96 96"
        fill="none"
        className="relative"
        aria-hidden
        focusable="false"
      >
        {/* The phone. Stroked rather than filled so it sits on the tinted panel
            in both themes without a fill that has to be defined twice. */}
        <rect
          x="30" y="20" width="36" height="60" rx="8"
          fill="var(--color-surface)"
          stroke="var(--color-border-strong)"
          strokeWidth="2"
        />
        <rect x="41" y="25" width="14" height="2.5" rx="1.25" fill="var(--color-border-strong)" />

        {/* The notification, landing on it — the whole subject of the card, so
            it is the only filled element and the only one carrying the accent. */}
        <rect x="18" y="42" width="60" height="22" rx="7" fill="var(--color-action)" />
        <circle cx="30" cy="53" r="5.5" fill="var(--color-surface)" fillOpacity="0.9" />
        {/* A bell inside the disc, at the size it is legible: a clapper and a
            hood, nothing more. Any more detail disappears at 11px across. */}
        <path
          d="M27.6 54.4c0-1.9.5-2.6 1.1-3.2.3-.3.4-.6.4-1.1a.9.9 0 0 1 1.8 0c0 .5.1.8.4 1.1.6.6 1.1 1.3 1.1 3.2h-4.8Z"
          fill="var(--color-action)"
        />
        <path d="M29.3 55.4h1.4a.7.7 0 0 1-1.4 0Z" fill="var(--color-action)" />

        {/* Two bars standing in for the notification's text. Deliberately not
            letters — see the note above. */}
        <rect x="40" y="47" width="30" height="3.5" rx="1.75" fill="var(--color-surface)" fillOpacity="0.95" />
        <rect x="40" y="54" width="20" height="3.5" rx="1.75" fill="var(--color-surface)" fillOpacity="0.6" />
      </svg>
    </div>
  );
}
